import { useMemo } from 'react'

import { ICONS } from '@/components/icons/icons'
import { Panel } from '@/components/ui/Panel'
import { ErrorState, Skeleton } from '@/components/ui/StateMessage'

import { useTransactions } from '../hooks/useTransactions'
import { cumulativeSeries, isPeriodComplete } from '../lib/pace'
import { NON_CONTIGUOUS_NOTE } from '../lib/periodText'
import { useRunwayModel } from '../RunwayContext'
import { PaceChart } from './PaceChart'
import { PaceLegend } from './PaceLegend'
import styles from './PacePanel.module.css'

interface PacePanelProps {
  title: string
  codes: readonly string[]
  budget: number
  hint?: string
}

/**
 * The cumulative pacing chart. Lines are loaded per department; when some departments fail, the
 * chart still renders from the others and a short note names the missing ones with a retry.
 */
export function PacePanel({ title, codes, budget, hint }: PacePanelProps) {
  const { runway, calendar, deptName } = useRunwayModel()
  // A pick of months that are not adjacent has no single time axis: a note instead of the chart.
  const { rows, isLoading, isError, failedCodes, retry } = useTransactions(codes, calendar.contiguous)
  const complete = isPeriodComplete(runway)
  const series = useMemo(
    () => cumulativeSeries(rows, calendar, runway.days_elapsed_in_quarter),
    [rows, calendar, runway.days_elapsed_in_quarter],
  )

  let body
  if (!calendar.contiguous) body = <p className={styles.note}>{NON_CONTIGUOUS_NOTE}</p>
  else if (isError)
    body = (
      <ErrorState>
        Could not load transactions for the pacing chart.{' '}
        <button type="button" className={styles.retry} onClick={retry}>
          <ICONS.refresh size={12} />
          Try again
        </button>
      </ErrorState>
    )
  else if (isLoading) body = <Skeleton label="Loading spending pace" tall />
  else
    body = (
      <>
        <PaceChart
          series={series}
          budget={budget}
          calendar={calendar}
          quarterLabel={runway.quarter_label}
          complete={complete}
          markerLabel={runway.period.is_live ? 'today' : 'as of'}
        />
        {failedCodes.length > 0 ? (
          <div className={styles.partial} role="status">
            <ICONS.warning size={13} className={styles.partialIcon} />
            <span>Chart excludes {failedCodes.map(deptName).join(', ')}: their transactions could not be loaded.</span>
            <button type="button" className={styles.retry} onClick={retry}>
              <ICONS.refresh size={12} />
              Retry
            </button>
          </div>
        ) : null}
      </>
    )

  return (
    <Panel title={title} aside={<PaceLegend hint={hint} complete={complete} />}>
      {body}
    </Panel>
  )
}
